import React, { useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { GraduationCap, Mail, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { useNoIndex } from "@/lib/useSeo";

export default function ForgotPassword() {
  useNoIndex();
  const [email, setEmail] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/auth/forgot-password", { email });
      setSubmitted(true);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Erreur");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid md:grid-cols-2" data-testid="forgot-password-page">
      <div className="flex flex-col justify-center px-8 md:px-16 py-12 bg-white">
        <div className="max-w-sm w-full mx-auto">
          <Link to="/login" className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-900 mb-8" data-testid="back-to-login">
            <ArrowLeft size={14} /> Retour à la connexion
          </Link>
          <div className="flex items-center gap-2 mb-8">
            <div className="w-10 h-10 rounded-xl bg-indigo-500 text-white flex items-center justify-center">
              <GraduationCap size={22} />
            </div>
            <span className="font-display text-xl font-bold text-slate-900">Student Hub</span>
          </div>
          <h1 className="font-display text-3xl sm:text-4xl font-bold text-slate-900 mb-2">Mot de passe oublié</h1>
          <p className="text-slate-500 mb-8">Saisis ton email, on t'envoie un lien pour le réinitialiser (valable 1 heure).</p>
          {submitted ? (
            <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-5 flex gap-3" data-testid="forgot-confirmation">
              <Mail size={20} className="text-emerald-600 mt-0.5 shrink-0" />
              <div>
                <p className="font-medium text-emerald-900">Email envoyé (si l'adresse existe)</p>
                <p className="text-sm text-emerald-800 mt-1">Vérifie ta boîte mail (et le spam). Le lien expire dans 1 heure.</p>
              </div>
            </div>
          ) : (
            <form onSubmit={onSubmit} className="space-y-5" data-testid="forgot-form">
              <div>
                <Label htmlFor="email">Email</Label>
                <Input id="email" type="email" required value={email}
                       onChange={(e) => setEmail(e.target.value)} className="mt-1.5" data-testid="forgot-email-input" />
              </div>
              <Button type="submit" disabled={loading} className="w-full bg-indigo-500 hover:bg-indigo-600 h-11" data-testid="forgot-submit-btn">
                {loading ? "Envoi..." : "M'envoyer un lien"}
              </Button>
            </form>
          )}
        </div>
      </div>
      <div className="hidden md:block bg-cover bg-center relative"
           style={{ backgroundImage: "url('https://images.unsplash.com/photo-1677594332295-affd04f83115?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600')" }}>
        <div className="absolute inset-0 bg-gradient-to-br from-indigo-600/30 via-indigo-500/10 to-yellow-200/20" />
      </div>
    </div>
  );
}
